import { useEffect, useState } from 'react';
import type { Locale } from '../components/LangToggle';
import type { Theme } from '../components/ThemeToggle';

const LOCALE_KEY = 'bassitai.locale';
const THEME_KEY = 'bassitai.theme';

function readLocale(): Locale {
  if (typeof window === 'undefined') return 'en';
  const stored = window.localStorage.getItem(LOCALE_KEY);
  if (stored === 'en' || stored === 'ar') return stored;
  const lang = (window.navigator?.language || 'en').toLowerCase();
  return lang.startsWith('ar') ? 'ar' : 'en';
}

function readTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(THEME_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return 'light'; // default OFF (light) per spec
}

/**
 * Source of truth for locale and theme on the login app.
 *
 * - Persists to localStorage so the choice survives reload.
 * - Reflects current state on <html> via data-theme, dir, and lang attrs
 *   so tokens.css and global selectors flip correctly.
 */
export function useThemeAndLocale() {
  const [locale, setLocale] = useState<Locale>(() => readLocale());
  const [theme, setTheme] = useState<Theme>(() => readTheme());

  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('data-theme', theme);
    window.localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    const html = document.documentElement;
    if (locale === 'ar') {
      html.setAttribute('dir', 'rtl');
      html.setAttribute('lang', 'ar');
    } else {
      html.setAttribute('dir', 'ltr');
      html.setAttribute('lang', 'en');
    }
    window.localStorage.setItem(LOCALE_KEY, locale);
  }, [locale]);

  return { locale, setLocale, theme, setTheme };
}
